import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { PageShell } from "../../shared/layouts/PageShell";
import { ApiError } from "../../../services/apiClient";
import { useAuth } from "../../../providers/AuthProvider";
import { SurfaceCard } from "../../shared/ui/surface/SurfaceCard";
import { InfoBanner } from "../../../components/InfoBanner";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { Avatar } from "../../shared/ui/Avatar";

import { ArrowLeft, ArrowRight, EnvelopeSimple } from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";

type VerificationState =
  | { kind: "pending" }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const { completeEmailVerification, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [state, setState] = useState<VerificationState>({ kind: "pending" });
  const breadcrumbItems = useMemo<BreadcrumbItem[]>(
    () => [{ label: t("auth.verify_email.breadcrumb", "Verify email"), to: "/verify-email" }],
    [t]
  );
  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    includeHome: true,
    fallback: { to: user ? "/dashboard" : "/login" }
  });

  useEffect(() => {
    if (!token) {
      setState({
        kind: "error",
        message: t("auth.verify_email.missing_token")
      });
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const response = await completeEmailVerification(token);
        if (!isMounted) return;
        if (
          response.user.email_verified &&
          response.user.phone_verified
        ) {
          navigate("/welcome/credits", { replace: true });
          return;
        }
        setState({
          kind: "success",
          message: t("auth.verify_email.success")
        });
      } catch (err) {
        if (!isMounted) return;
        if (
          err instanceof ApiError &&
          (err.message?.toLowerCase().includes("already been used") ||
            err.message?.toLowerCase().includes("already verified") ||
            err.message?.toLowerCase().includes("no pending email change"))
        ) {
          setState({
            kind: "success",
            message: t("auth.verify_email.already_used")
          });
        } else {
          const message =
            err instanceof ApiError
              ? err.message
              : t("auth.verify_email.invalid_token");
          setState({
            kind: "error",
            message
          });
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [completeEmailVerification, token, t, navigate]);

  const userCard = user ? (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/80 px-3 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
      <Avatar
        name={user.name}
        email={user.email}
        photoUrl={user.profile_photo?.download_url ?? null}
        fallbackUrl={user.profile_photo?.remote_url ?? null}
        size={44}
        className="shadow-sm ring-1 ring-slate-100 dark:ring-slate-700"
      />
      <div className="flex flex-col leading-tight">
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {user.name || user.email}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">{user.email}</span>
      </div>
    </div>
  ) : null;

  const cta = useMemo(() => {
    if (state.kind === "success") {
      return {
        label: t("navigation.to_dashboard"),
        action: () => navigate("/dashboard", { replace: true }),
        icon: <ArrowRight {...defaultIconProps} aria-hidden />
      };
    }
    return {
      label: user ? t("navigation.to_profile") : t("navigation.to_login"),
      action: () => navigate(user ? "/settings/profile" : "/login", { replace: true }),
      icon: <ArrowLeft {...defaultIconProps} aria-hidden />
    };
  }, [state.kind, navigate, user, t]);

  const title =
    state.kind === "pending"
      ? t("auth.verify_email.title_pending", "Verifying your email")
      : state.kind === "success"
        ? t("auth.verify_email.title_success")
        : t("auth.verify_email.title_error");
  const subtitle =
    state.kind === "pending"
      ? t(
          "auth.verify_email.subtitle_pending",
          "Hang tight while we confirm your link. This keeps your account and support messages secure."
        )
      : t(
          "auth.verify_email.subtitle_generic",
          "Email verification keeps your surveys, billing, and support conversations protected."
        );

  const statusTone =
    state.kind === "pending"
      ? "info"
      : state.kind === "success"
        ? "success"
        : "warning";
  const statusMessage =
    state.kind === "pending" ? t("auth.verify_email.verifying") : state.message;

  const primaryIcon =
    state.kind === "success" ? (
      <ArrowRight {...defaultIconProps} aria-hidden />
    ) : state.kind === "pending" ? (
      <EnvelopeSimple {...defaultIconProps} aria-hidden />
    ) : (
      <ArrowLeft {...defaultIconProps} aria-hidden />
    );

  return (
    <PageShell width="narrow" className="flex flex-col gap-6">
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {userCard}
            <button
              type="button"
              className="pm-btn pm-btn-ghost pm-btn-md"
              onClick={handleBack}
            >
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />
      <SurfaceCard className="w-full">
        <SurfaceCard.Body gap="base" textTone="default" className="text-center">
          <div className="flex flex-col items-center gap-3">
            <span
              className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-100"
              aria-hidden
            >
              {primaryIcon}
            </span>
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {title}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-300">{statusMessage}</p>
            </div>
          </div>
        </SurfaceCard.Body>
        <SurfaceCard.Footer justify="center" gap="base" divider>
          <button
            type="button"
            className="pm-btn pm-btn-primary w-full sm:w-auto"
            onClick={cta.action}
            disabled={state.kind === "pending"}
          >
            {cta.icon}
            {cta.label}
          </button>
        </SurfaceCard.Footer>
      </SurfaceCard>
      <InfoBanner
        tone={statusTone === "success" ? "success" : statusTone === "info" ? "info" : "warning"}
        title={title}
        description={statusMessage}
      >
        {state.kind !== "success" ? (
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/settings/profile#profile-email"
              className="pm-btn pm-btn-secondary pm-btn-sm"
            >
              {t("auth.verify_email.verify_email_cta")}
            </Link>
            <Link to="/login" className="pm-btn pm-btn-ghost pm-btn-sm">
              {t("navigation.to_login")}
            </Link>
          </div>
        ) : null}
      </InfoBanner>
    </PageShell>
  );
}
